// lib/types/property.ts
// Auction Engine v2.2 – Property Types
// Last Updated: 2025-11-13

export enum PropertyCategory {
  Residential = "residential",
  Commercial = "commercial",
}

export enum PropertyType {
  // 주거용 (기존)
  Apartment = "apartment",
  Villa = "villa",
  Officetel = "officetel",
  MultiHouse = "multi_house",
  Detached = "detached",
  ResidentialLand = "res_land",
  
  // 주거용 (크롤링 추가)
  MultiFamily = "multi_family", // 다세대
  RowHouse = "row_house", // 연립주택
  MixedUse = "mixed_use", // 주상복합
  
  // 상업용 (기존)
  Store = "store",
  Office = "office",
  Factory = "factory",
  Warehouse = "warehouse",
  CommercialLand = "com_land",
  
  // 상업용 (크롤링 추가)
  StoreHouse = "store_house", // 상가주택
  NeighborhoodFacility = "neighborhood_facility", // 근린생활시설
  RetailFacility = "retail_facility", // 판매시설
  SportsFacility = "sports_facility", // 운동시설
  LodgingFacility = "lodging_facility", // 숙박시설
}

export enum DifficultyMode {
  Easy = "easy",
  Normal = "normal",
  Hard = "hard",
}

/**
 * 토지 지분 정보
 * - 단독주택/다가구/빌라: 토지 지분 (전부, 1/2, 1/3 등)
 * - 아파트/오피스텔: 대지권 비율 (전부, 1/10, 1/20 등)
 */
export interface LandShareRatio {
  numerator: number; // 분자 (예: 1)
  denominator: number; // 분모 (예: 2 → 1/2 지분)
}

export interface LandInfo {
  /** 토지 지분 (예: "전부", { numerator: 1, denominator: 2 }) - 아파트/오피스텔은 없음 */
  shareRatio?: LandShareRatio | "전부";

  /** 대지권 비율 (아파트/오피스텔용, 선택적) */
  surfaceRightRatio?: LandShareRatio | "전부";

  /** 토지 용도 (예: "대지", "전", "잡종지") */
  landUse?: string;
}

/**
 * 제시외 건물 정보
 * - 창고, 차고, 부속건물 등 별도 평가되는 건물
 */
export interface ExcludedBuilding {
  /** 제시외 건물 종류 (예: "창고", "차고", "부속건물") */
  type: string;

  /** 면적 (㎡) */
  sizeM2: number;

  /** 구조/용도 (예: "철근콘크리트조", "목조") */
  structure?: string;

  /** 감정가 (PropertyEngine에서 계산) */
  appraisalValue: number;
}

export interface PropertySeed {
  id?: string;
  category: PropertyCategory;
  type: PropertyType;
  sizeM2: number;
  landSizeM2?: number;
  yearBuilt?: number;
  floorInfo?: { total: number | null; current: number | null };
  address?: string;
  auctionStep?: number;
  difficulty: DifficultyMode;
  buildingUse?: string;
  volatilityMultiplier?: number; // 시세 변동성 배수 (volatileMarket 옵션용)

  /** 토지 정보 (토지 지분, 대지권, 용도) */
  landInfo?: LandInfo;

  /** 제시외 건물 목록 */
  excludedBuildings?: ExcludedBuilding[];
}

export interface Property {
  id: string;
  category: PropertyCategory;
  type: PropertyType;
  sizeM2: number;
  landSizeM2: number | null;
  yearBuilt: number | null;
  appraisalValue: number; // 건물 감정가 (기존 호환성 유지)
  auctionStep: number;
  address: string;
  difficulty: DifficultyMode;
  floorInfo: { total: number | null; current: number | null };
  buildingUse: string;
  volatilityMultiplier?: number; // 시세 변동성 배수 (volatileMarket 옵션용)

  /** 토지 정보 (토지 지분, 대지권, 용도) */
  landInfo: LandInfo | null;

  /** 제시외 건물 목록 */
  excludedBuildings: ExcludedBuilding[];

  /** 토지 감정가 (별도 계산) */
  landAppraisalValue: number | null;

  /** 총 감정가 (건물 + 토지 + 제시외 건물) */
  totalAppraisalValue: number;

  /** 청구금액 (배당금 등) */
  claimAmount: number;
}
